import {useIssueCreateActions, useIssueCreateMetadata} from '@github-ui/issue-create/IssueCreateDataContext'
import type {IssueCreatePayload} from '@github-ui/issue-create/Model'
import type {IssueFormRef} from '@github-ui/issue-form/Types'
import {CommandButton} from '@github-ui/ui-commands'
import {Dialog} from '@primer/react'
import React, {type RefObject, useCallback} from 'react'

import type {DraftOrExistingIssue} from '../content-preview-types'
import {BulkCreateButton} from './BulkCreateButton'
import type {TreeNode} from './use-draft-issue-tree-map'
import type {SingleIssueCreateError} from './use-issue-creation-handler'
import {useIssueCreationHandler} from './use-issue-creation-handler'

export type CreateIssueButtonProps = {
  issueTemplate: IssueCreatePayload | null | undefined
  issueNode: TreeNode<DraftOrExistingIssue> | undefined
  issueFormRef: RefObject<IssueFormRef | null>
  noRepoAccess: boolean
  onClose: () => void
  onError?: (error: SingleIssueCreateError) => void
  onClearError?: () => void
}

export function CreateIssueButton({
  issueTemplate,
  issueNode,
  issueFormRef,
  noRepoAccess = false,
  onClose,
  onError,
  onClearError,
}: CreateIssueButtonProps) {
  const [dialogOpen, setDialogOpen] = React.useState(false)
  const buttonRef = React.useRef<HTMLButtonElement>(null)
  const [isSubmitting, setIsSubmitting] = React.useState(false)

  const {repository: formRepository} = useIssueCreateMetadata()
  const templateRequired = formRepository && !issueTemplate && !formRepository.isBlankIssuesEnabled

  const {clearSessionData} = useIssueCreateActions()

  const onClearSession = useCallback(() => {
    clearSessionData()
    issueFormRef.current?.clearSessionStorage()
  }, [clearSessionData, issueFormRef])

  // Clears session storage and closes the content preview window.
  // This mirrors the cleanup logic that we pass into CreateIssueForm.
  const clearOnCreate = useCallback(() => {
    onClearSession()
    onClose?.()
    setIsSubmitting(false)
  }, [onClearSession, onClose])

  // Handle error callback
  const handleError = useCallback(
    (error: SingleIssueCreateError) => {
      onError?.(error)
      setIsSubmitting(false)
    },
    [onError],
  )

  // Use the unified issue creation handler
  const {handleCreate: handleSingleCreate} = useIssueCreationHandler({
    issueNode,
    onComplete: clearOnCreate,
    onError: handleError,
    issueFormRef,
    issueTemplate,
    formRepository,
  })

  // If this issue is part of a tree (root or otherwise),
  // save is handled in a confirmation/blocking dialog.
  if (issueNode?.parent || issueNode?.children.length) {
    return <BulkCreateButton issueNode={issueNode} disabled={noRepoAccess} onSuccess={clearOnCreate} />
  }

  // Don't render button if no repository is selected or if issues are disabled
  if (!formRepository || !formRepository.hasIssuesEnabled) {
    return null
  }

  if (templateRequired) {
    return (
      <>
        <CommandButton
          commandId="github:submit-form"
          variant="primary"
          data-testid="create-issue-dialog-button"
          ref={buttonRef}
          showKeybindingHint
          onClick={e => {
            e.preventDefault()
            setDialogOpen(true)
          }}
        >
          Create
        </CommandButton>
        {dialogOpen && (
          <Dialog
            title="Select a template"
            onClose={() => setDialogOpen(false)}
            footerButtons={[
              {
                buttonType: 'primary',
                content: 'OK',
                onClick: () => setDialogOpen(false),
              },
            ]}
            returnFocusRef={buttonRef}
          >
            This repository requires all issues to have a template. To create this issue, first select the appropriate
            template from the template dropdown.
          </Dialog>
        )}
      </>
    )
  }

  // Handle single creation with bulk job
  return (
    <>
      <CommandButton
        commandId="github:submit-form"
        variant="primary"
        data-testid="single-create-issue-button"
        ref={buttonRef}
        inactive={isSubmitting}
        showKeybindingHint
        onClick={e => {
          e.preventDefault()
          onClearError?.() // Reset error state on resubmission
          setIsSubmitting(true)
          handleSingleCreate()
        }}
      >
        Create
      </CommandButton>
    </>
  )
}
